import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import "./shared/locales/setup.ts";
import { initSentry } from "@shared/services/sentry";
import { setupInterceptors } from "@shared/api";
import { getActiveQueryClient } from "@app/providers";
import { useAuthStore } from "@app/store";
import { PagesRoutes } from "@app/router";

// Must run before createRoot so Sentry captures early errors and wraps the router
initSentry();

/**
 * Hand the API client the two things it cannot reach on its own.
 *
 * Injected rather than imported: `shared/api` must not depend on `@app/*`.
 * Routing the session through the store (instead of letting the transport
 * touch localStorage itself) keeps one writer for the session — a renewed
 * token lands in memory and in storage together.
 */
setupInterceptors({
  session: {
    getAccessToken: () => useAuthStore.getState().token,
    getRefreshToken: () => useAuthStore.getState().refreshToken,
    setTokens: ({ token, refreshToken }) =>
      useAuthStore.setState({ token, refreshToken }),
  },
  /**
   * The client handles renewal itself — it will spend the refresh token and
   * replay the request before this fires. Reaching here means the refresh
   * token is gone or was rejected too, so the only honest move is to end the
   * session.
   *
   * The redirect is a hard location change because this can fire from outside
   * the React tree, where the router's navigate is unavailable.
   */
  onUnauthorized: () => {
    useAuthStore.getState().logout();
    // Without this the next sign-in on this browser would briefly render the
    // previous user's cached data.
    getActiveQueryClient()?.clear();

    if (window.location.pathname !== PagesRoutes.auth.login) {
      window.location.assign(PagesRoutes.auth.login);
    }
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
